// Local Imports
import { Point, Direction } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
import * as modes from "../../modes";
import * as changes from "DrawCore/src/changes";
import type { InteractionHost } from "../../interaction_host";
import { Keys, hasPlatformCmdModifier } from "../../keys";
import type { LayoutContent } from "./content";
import { InstanceEntity, Segment } from "DrawCore/src/content/layout/instance";
import { PinEntity } from "DrawCore/src/content/layout/pin";
import { SegmentEntity } from "DrawCore/src/content/layout/route";
import { DrawRoute } from "./draw_route";
import { routingGridSnap } from "DrawCore/src/content/layout/stack";
import {
  hitTestOutlineEdge,
  edgeTolerance,
  ResizeOutline,
} from "./resize_outline";

// # Idle Mode
export class LayoutIdle extends modes.ModeHandler {
  // Track the currently hovered pin so we can un-hover it when the mouse moves away.
  private hoveredPin: PinEntity | null = null;

  constructor(readonly layout: LayoutContent) {
    super();
  }
  get editor() {
    return this.layout.editor;
  }
  // We're already in idle mode.
  // On `abort`, just de-select anything highlighted.
  override abort = () => {
    this.clearHover();
    this.editor.content.deselect();
  };

  // Clear the current hover state.
  private clearHover = (): void => {
    if (this.hoveredPin) {
      this.hoveredPin.hovered = false;
      this.hoveredPin = null;
    }
  };

  // Update hover highlight on mouse move.
  override handleMouseMove = () => {
    const { editor, layout } = this;
    const hit = layout.whatdWeHit(editor.uiState.mousePos);
    const pin = hit instanceof PinEntity ? hit : null;

    // No change — skip redraw
    if (pin === this.hoveredPin) return null;

    // Clear old hover
    this.clearHover();

    // Set new hover (only if not already selected)
    if (pin && !pin.highlighted) {
      pin.hovered = true;
      this.hoveredPin = pin;
    }

    return editor.requestRedraw();
  };

  // In idle mode, if we clicked on something, react and update our UI state.
  override handleMouseDown = (e: MouseEvent) => {
    const { editor, layout } = this;

    // If a context menu is open, this click is dismissing it — don't start any interaction.
    if (editor.isAnyContextMenuVisible()) return;

    // Ctrl+Middle click: drag to zoom
    if (e.button === 1 && e.ctrlKey) {
      editor.modeHandler = modes.DragZoom.start(editor);
      return;
    }

    // Middle click: ALWAYS start panning, regardless of what's under cursor
    if (e.button === 1) {
      editor.modeHandler = modes.Pan.start(editor);
      return;
    }

    // Check outline edges first (left-click only, when outlines are enabled).
    // This runs before whatdWeHit so outline edge clicks take priority over instance selection.
    if (e.button === 0 && !hasPlatformCmdModifier(e) && editor.showOutlines) {
      const outlineHit = this.hitTestCellOutline();
      if (outlineHit) return outlineHit;
    }

    // Hit test, finding which element was clicked on.
    const whatd_we_hit = layout.whatdWeHit(editor.uiState.mousePos);
    if (!whatd_we_hit) {
      // Right-click on empty space: show empty-space menu. Blockages are
      // system-managed (v2 redesign §5) so right-clicking one falls
      // through to the empty-space menu — no blockage context menu.
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        // Get the grid-snapped position for instance creation
        const gridPoint = editor.gridPointNearestToMouse();
        editor.emptySpaceContextMenu.show(
          e.pageX,
          e.pageY,
          gridPoint.x,
          gridPoint.y
        );
        return;
      }

      // Command-click (Cmd on Mac, Ctrl on Win/Linux): start panning.
      if (hasPlatformCmdModifier(e)) {
        editor.modeHandler = modes.Pan.start(editor);
        return;
      }

      // Hit "blank space". Start box-select (drag to select entities).
      // If the user clicks without dragging, this effectively deselects.
      return modes.BoxSelect.enter(editor, e.shiftKey);
    }

    // Routes are not interactive in idle mode — only in DrawRoute mode (press R).
    // whatdWeHit is called with includeRoutes=false so this guard should never
    // trigger, but it narrows the type for the checks below.
    if (whatd_we_hit instanceof SegmentEntity) return;

    // Resolve the Selectable to its owning Child for selection tracking.
    const child = layout.resolveChild(whatd_we_hit);

    // Right-click: show context menu
    if (e.button === 2) {
      e.preventDefault();
      e.stopPropagation(); // Prevent global handlers from closing menu immediately
      // Ensure the right-clicked item is in the selection
      if (child && !layout.selection.has(child)) {
        if (e.shiftKey) {
          layout.addToSelection(child);
        } else {
          layout.select(child);
        }
      }
      // Pins get their own context menu with Edit/Delete actions.
      if (whatd_we_hit instanceof PinEntity) {
        editor.layoutPinContextMenu.show(whatd_we_hit.data, e.pageX, e.pageY);
        return;
      }
      editor.instanceContextMenu.show(
        whatd_we_hit,
        e.pageX,
        e.pageY,
        layout.selection.size
      );
      return;
    }

    // Shift+click: toggle the entity in the multi-selection set.
    if (e.shiftKey && child) {
      // Shift-click on a segment: toggle that specific segment.
      // resolveChild maps Segment → parent InstanceEntity, so this cast is safe.
      if (whatd_we_hit instanceof Segment) {
        return layout.addSegmentToSelection(
          child as InstanceEntity,
          whatd_we_hit.index
        );
      }
      return layout.addToSelection(child);
    }

    // Clicked on an already-selected entity in a multi-selection: move the whole selection.
    if (child && layout.selection.has(child) && layout.selection.size > 1) {
      return modes.MoveSelection.enter(editor, true, false, true);
    }

    // Clicked on a segment of an already-selected instance.
    if (whatd_we_hit instanceof Segment && child) {
      const inst = child as InstanceEntity;
      const selectedIndices = layout.getSelectedSegmentIndices(inst);
      if (selectedIndices && selectedIndices.has(whatd_we_hit.index)) {
        // Segment is already selected — drag all selected segments of this instance
        return modes.MoveSegments.enter(editor, inst, selectedIndices);
      }
      if (!selectedIndices && layout.selection.has(inst)) {
        // Whole-instance selected (e.g. via "Select All Segments") — move whole instance
        return modes.MoveSelection.enter(editor, true, false, true);
      }
      // New segment click — single-select this segment and start dragging it
      layout.selectSegment(inst, whatd_we_hit.index);
      return modes.MoveSegments.enter(
        editor,
        inst,
        new Set([whatd_we_hit.index])
      );
    }

    // Single select this entity.
    if (child) layout.select(child);

    // Pins have only shapes (not a full xform), so use MoveLayoutPin instead.
    if (whatd_we_hit instanceof PinEntity) {
      // Clear hover before switching modes — the new LayoutIdle won't know about it.
      this.clearHover();
      whatd_we_hit.hovered = false;
      return MoveLayoutPin.enter(editor, layout, whatd_we_hit);
    }

    return modes.MoveLayoutXformable.enter(editor, whatd_we_hit);
  };
  // Check the current cell's outline edges.
  // Returns a ResizeOutline mode entry if an edge was hit, or null.
  // Only active when the cell has an explicit outline set.
  private hitTestCellOutline = (): ResizeOutline | null => {
    const { editor, layout } = this;
    const outline = layout.data.abs.outline;
    if (!outline) return null;

    const tolerance = edgeTolerance(editor);
    const mousePosSch = editor.uiState.mousePos.sch;
    const edge = hitTestOutlineEdge(outline, mousePosSch, tolerance);
    if (edge) {
      return ResizeOutline.enter(editor, layout, edge, outline);
    }
    return null;
  };

  // Handle keystrokes.
  override handleKey = (e: KeyboardEvent) => {
    const { editor, layout } = this;
    switch (e.key) {
      case Keys.R:
        return DrawRoute.enter(editor);
      case Keys.m:
        return modes.RulerAnchor.enter(editor);
      // Box-select mode (keyboard entry: waits for first click)
      case Keys.b:
        return modes.BoxSelectWait.enter(editor);
      // Additive box-select mode (Shift+B: adds to existing selection)
      case Keys.B:
        return modes.BoxSelectWait.enter(editor, true);
      // Zoom-to-box mode (Z key: draw box, then zoom to fit it)
      case Keys.z:
        return modes.ZoomToBoxWait.enter(editor);
      // Grab / move selection
      case Keys.g:
        if (layout.selection.size > 0) {
          return modes.MoveSelection.enter(editor);
        }
        return;
      case Keys.r:
        if (layout.selection.size > 0) {
          const count = layout.selection.size;
          const result = layout.rotateSelected();
          if (result) editor.showItemFeedback("Rotated", count);
          return result;
        }
        return;
      case Keys.v:
        if (layout.selection.size > 0) {
          const count = layout.selection.size;
          const result = layout.flipSelected(Direction.Vert);
          if (result) editor.showItemFeedback("Flipped", count, " vertically");
          return result;
        }
        return;
      case Keys.h:
        if (layout.selection.size > 0) {
          const count = layout.selection.size;
          const result = layout.flipSelected(Direction.Horiz);
          if (result)
            editor.showItemFeedback("Flipped", count, " horizontally");
          return result;
        }
        return;
      case Keys.Delete:
      case Keys.Backspace: {
        const count = layout.selection.size;
        const result = layout.deleteSelected();
        if (result) editor.showItemFeedback("Deleted", count);
        return result;
      }
      default:
        return null;
    }
  };
}

// # MoveLayoutPin
//
// Click-drag to move a layout pin. Pins have only shapes (not a full
// LayoutXform), so this is simpler than instance movement: every shape moves
// with the pin's anchor (the centre of its shapes' box). Snaps the anchor to
// the routing grid (metal layer pitch) rather than the placement grid.
//
class MoveLayoutPin extends modes.ModeHandler {
  private readonly origLoc: Point;
  private readonly origShapes: schema.layout.LayoutPin["shapes"];
  private readonly initialRelativeMousePos: Point;
  // Saved placement grid settings, restored on commit/abort.
  private readonly savedGridSettings: { snapX: number; snapY: number };

  private constructor(
    private readonly editor: InteractionHost,
    private readonly layout: LayoutContent,
    private readonly pin: PinEntity
  ) {
    super();
    this.origLoc = Point.clone(pin.anchor);
    this.origShapes = pin.data.shapes;
    const { sch } = editor.uiState.mousePos;
    this.initialRelativeMousePos = Point.sub(this.origLoc, sch);

    // Switch to the routing grid so the pin lands on a track intersection the
    // auto-router will also use (per-axis pitch: vertical layer constrains X,
    // horizontal layer constrains Y). The old code snapped both axes to the
    // single minimum pitch across all layers, which put manually-moved pins on
    // a different lattice than routed geometry whenever the two pitches differ.
    const { snapX, snapY } = editor.grid.settings;
    this.savedGridSettings = { snapX, snapY };
    const metals = layout.stack?.metalLayers;
    // Fall back to the current (placement) grid if the stack lacks a layer in
    // either direction or has an invalid pitch, rather than snapping to bogus
    // coordinates.
    const routingSnap = metals ? routingGridSnap(metals) : null;
    if (routingSnap) {
      editor.grid.updateSettings(routingSnap);
    }
  }

  static enter(
    editor: InteractionHost,
    layout: LayoutContent,
    pin: PinEntity
  ): unknown {
    return editor.setMode(new MoveLayoutPin(editor, layout, pin));
  }

  override handleMouseMove = () => {
    const { editor, initialRelativeMousePos, pin } = this;
    const { mousePos } = editor.uiState;
    const newLoc = editor.grid.nearestPoint(
      Point.add(mousePos.sch, initialRelativeMousePos)
    );
    return pin.update({ shapes: pin.shapesAnchoredAt(newLoc) });
  };

  override handleMouseUp = () => {
    const { editor, layout, pin, origLoc, origShapes } = this;
    const newLoc = pin.anchor;

    // Restore placement grid
    editor.grid.updateSettings(this.savedGridSettings);

    // Only log a change if the pin actually moved
    if (!Point.eq(origLoc, newLoc)) {
      layout.logChange(
        changes.update({
          target: pin,
          from: { shapes: origShapes },
          to: { shapes: pin.data.shapes },
        })
      );
    }
    editor.goUiIdle();
  };

  override abort = () => {
    // Restore placement grid
    this.editor.grid.updateSettings(this.savedGridSettings);
    this.pin.update({ shapes: this.origShapes });
    this.editor.deselect();
    this.editor.goUiIdle();
  };
}
